/**
 * Un GAN n'appartient qu'à une fiche du parc (#1235, #1236).
 *
 * - `describeGanHolder` résume la fiche qui porte déjà un GAN, quel que soit
 *   son état (active, décédée, partie, archivée — « supprimée » à l'écran).
 * - `buildZimsRestorePatch` décrit la restauration d'une fiche archivée quand
 *   l'animal revient par l'import de la collection ZIMS : la fiche garde son
 *   identité et son historique, elle est seulement remise dans la collection.
 *
 * Logique pure, sans Firestore : le connecteur `animalDatabaseConnector` lit et
 * écrit, les écrans traduisent `ganHolderStatus`.
 */

import { normalizeGan } from "./duplicateDetection";

export const GAN_HOLDER_STATUSES = Object.freeze({
  ACTIVE: "active",
  ARCHIVED: "archived",
  DECEASED: "deceased",
  DEPARTED: "departed",
});

/** GAN comparable : vide pour une valeur de remplissage (« N/C », « 0 »…). */
export const comparableGan = (gan) => normalizeGan(gan);

/** Le GAN saisi change-t-il réellement par rapport à celui de la fiche ? */
export const ganChanged = (previousGan, nextGan) =>
  comparableGan(nextGan) !== "" && comparableGan(nextGan) !== comparableGan(previousGan);

export const ganHolderStatus = (animal = {}) => {
  if (animal.isArchived === true) return GAN_HOLDER_STATUSES.ARCHIVED;
  if (animal.isDeceased === true) return GAN_HOLDER_STATUSES.DECEASED;
  if (animal.isDeparted === true) return GAN_HOLDER_STATUSES.DEPARTED;
  return GAN_HOLDER_STATUSES.ACTIVE;
};

/**
 * Parmi les fiches du parc qui portent un même GAN (doublons anciens possibles),
 * celle qui fait foi : une fiche non archivée d'abord, puis l'archivée la plus
 * récente. Ainsi l'import ZIMS ne restaure jamais une fiche archivée quand une
 * autre fiche du même animal est encore dans la collection.
 */
export const pickGanHolder = (candidates = []) => {
  const rank = (animal) => (ganHolderStatus(animal) === GAN_HOLDER_STATUSES.ARCHIVED ? 1 : 0);
  const archivedAt = (animal) => {
    const raw = Number(animal?.archivedDate);
    if (!Number.isFinite(raw)) return 0;
    return raw > 1e12 ? raw / 1000 : raw;
  };
  return [...candidates].sort((a, b) => rank(a.data) - rank(b.data) || archivedAt(b.data) - archivedAt(a.data))[0] || null;
};

/** Fiche porteuse du GAN, réduite à ce que les messages affichent. */
export const describeGanHolder = (animal = {}, docId = null) =>
  Object.freeze({
    animalId: animal.animalId || docId || null,
    animalName: animal.animalName || "",
    animalGan: String(animal.animalGan || "").trim(),
    status: ganHolderStatus(animal),
    archivedDate: animal.archivedDate ?? null,
  });

/**
 * Date d'archivage lisible (JJ/MM/AAAA). `archivedDate` est un horodatage Unix
 * (`moment().unix()`, en secondes), parfois stocké en chaîne.
 */
export const formatArchivedDate = (archivedDate) => {
  const raw = Number(archivedDate);
  if (!Number.isFinite(raw) || raw <= 0) return "";
  // Quelques écritures anciennes stockent des millisecondes.
  const date = new Date(raw > 1e12 ? raw : raw * 1000);
  const pad = (value) => String(value).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

/**
 * Libellé d'un conflit de GAN, dans la langue de l'écran :
 * « Le GAN X est déjà porté par « Nom » (fiche supprimée le 26/06/2025)… ».
 */
export const ganConflictMessage = (t, holder, gan) => {
  if (!holder) return "";
  const date = formatArchivedDate(holder.archivedDate);
  const status =
    holder.status === GAN_HOLDER_STATUSES.ARCHIVED
      ? date
        ? t("animal.ganConflict.status.archivedOn", { date })
        : t("animal.ganConflict.status.archived")
      : holder.status === GAN_HOLDER_STATUSES.ACTIVE
        ? ""
        : t(`animal.ganConflict.status.${holder.status}`);
  const values = { gan: gan || holder.animalGan, name: holder.animalName || "—", status };
  return status
    ? t("animal.ganConflict.textWithStatus", values)
    : t("animal.ganConflict.text", values);
};

/** Même forme minimale d'enclos que la création et l'import ZIMS. */
export const toAnimalEnclosureData = (rawEnclosure) =>
  rawEnclosure
    ? {
        enclosureId: rawEnclosure.enclosureId || rawEnclosure.value || null,
        enclosureName: rawEnclosure.enclosureName || rawEnclosure.label || null,
        linkedSectorId: rawEnclosure.linkedSectorId || null,
        value: rawEnclosure.value || rawEnclosure.enclosureId || null,
        label: rawEnclosure.label || rawEnclosure.enclosureName || null,
      }
    : null;

const isBlank = (value) => value === undefined || value === null || String(value).trim() === "";
const sexIsUnknown = (sex) => {
  const value = String(sex?.value ?? sex ?? "").trim().toLowerCase();
  return value === "" || value === "undefined" || value === "unknown";
};

/**
 * Restauration d'une fiche archivée par l'import ZIMS (#1236). L'animal est
 * dans la collection ZIMS du parc : il est vivant et présent. La fiche
 * reprend place (statuts remis à zéro, enclos choisi à l'import) ; son nom,
 * son espèce et son historique ne bougent pas. Seuls les champs vides sont
 * complétés par ZIMS (ID local, puce, date de naissance, sexe).
 */
export const buildZimsRestorePatch = (existing = {}, zims = {}, { restoredAt, restoredBy = null } = {}) => {
  const animalEnclosureData = toAnimalEnclosureData(zims.animalEnclosureData);
  const patch = {
    isArchived: false,
    isDeceased: false,
    isDeparted: false,
    animalEnclosureData,
    animalBelongToEnclosure: Boolean(animalEnclosureData),
    restoredFromArchive: {
      source: "zimsImport",
      restoredAt: restoredAt || new Date().toISOString(),
      restoredBy,
      previousArchivedDate: existing.archivedDate ?? null,
      previousArchivedBy: existing.archivedBy ?? null,
    },
  };
  if (isBlank(existing.animalLocalId) && !isBlank(zims.animalLocalId)) patch.animalLocalId = zims.animalLocalId;
  if (isBlank(existing.animalChipId) && !isBlank(zims.animalChipId)) patch.animalChipId = zims.animalChipId;
  if (isBlank(existing.animalBirthdate) && !isBlank(zims.animalDob)) patch.animalBirthdate = zims.animalDob;
  if (sexIsUnknown(existing.animalSexe) && !sexIsUnknown(zims.animalSexe)) patch.animalSexe = zims.animalSexe;
  return patch;
};
